import styled from '@emotion/styled'
import { ChangeEventHandler, useId } from 'react'

import { Div } from './Div'
import { Text } from './Text'
import { Color } from '../constants/color'
import { theme } from '../constants/theme'
import { StyleProps, createStyles } from '../utils/create-styles'

type ToggleSize = 'xs' | 'lg'

type Values = {
  button: number
  switchHeight: number
  switchWidth: number
  translate: number
}

const VALUES: Record<ToggleSize, Values> = {
  xs: {
    button: 12,
    switchHeight: 15,
    switchWidth: 30,
    translate: 16.5,
  },
  lg: {
    button: 22,
    switchHeight: 25,
    switchWidth: 50,
    translate: 25.5,
  },
}

const Body = styled.div({
  alignItems: 'center',
  display: 'flex',
  gap: 8,
})

const Button = styled.span<{ checked: boolean; size: ToggleSize }>((props) => ({
  background: Color.WHITE,
  borderRadius: 999,
  boxShadow: theme.boxShadow.normal,
  content: '""',
  height: VALUES[props.size].button,
  left: 0,
  position: 'absolute',
  transform: `translate(${
    props.checked ? VALUES[props.size].translate : 1.5
  }px, 1.5px)`,
  transition: theme.animation.transition,
  width: VALUES[props.size].button,
}))

const Checkbox = styled.input({
  height: 0,
  visibility: 'hidden',
  width: 0,
})

type ToggleLabelDesign = 'blue' | 'grey'

const Label = styled.label<{
  labelSize?: ToggleLabelSize
  design: ToggleLabelDesign
}>((props) => ({
  color: props.design === 'blue' ? Color.BLUE : Color.DARK_GREY,
  cursor: 'pointer',
  fontSize:
    props.labelSize === 'small' ? theme.font.size[6] : theme.font.size[5.5],
  fontWeight:
    props.labelSize === 'small'
      ? theme.font.weight.medium
      : props.design === 'blue'
        ? theme.font.weight.bold
        : 'grey',
}))

const Switch = styled.label<{
  checked: boolean
  disabled?: boolean
  size: ToggleSize
}>((props) => ({
  backgroundColor: props.disabled
    ? Color.LIGHT_GREY
    : props.checked
      ? Color.BLUE
      : Color.GREY,
  borderRadius: 999,
  cursor: props.disabled ? 'not-allowed' : 'pointer',
  height: VALUES[props.size].switchHeight,
  position: 'relative',
  transition: `background-color ${theme.animation.transition}`,
  width: VALUES[props.size].switchWidth,
}))

const SwitchLabel = styled.label({
  cursor: 'pointer',
  userSelect: 'none',
})

const ToggleElement = styled.div<{
  compact?: boolean
  direction: 'column' | 'row' | 'row-reverse'
  styles?: StyleProps
}>((props) => ({
  alignItems: 'center',
  display: 'flex',
  flexDirection: props.direction,
  gap: 12,
  justifyContent: props.compact ? 'flex-start' : 'space-between',
  userSelect: 'none',
  ...createStyles(props.styles),
}))

export type ToggleProps = {
  checked: boolean
  checkedLabel?: string
  compact?: boolean
  direction?: 'column' | 'row' | 'row-reverse'
  disabled?: boolean
  label?: string
  labelSize?: ToggleLabelSize
  /**
   * @deprecated use `onCheckedChange`
   */
  onChange?: ChangeEventHandler
  /**
   * TODO(rb): rename to "onChange" when that method has been successfully deprecated
   */
  onCheckedChange: (value: boolean) => void
  styles?: StyleProps
  subtitle?: string
  uncheckedLabel?: string
  size?: ToggleSize
  labelDesign?: ToggleLabelDesign
}

export type ToggleLabelSize = 'small' | 'medium'

export const Toggle = ({
  size = 'lg',
  labelDesign = 'blue',
  ...props
}: ToggleProps) => {
  const id = useId()

  return (
    <ToggleElement
      compact={props.compact}
      direction={props.direction ?? 'column'}
      styles={props.styles}
    >
      {(props.label || props.subtitle) && (
        <Div styles={{ flex: 'col', gap: 1 }}>
          {props.label && (
            <Label
              htmlFor={id}
              labelSize={props.labelSize}
              design={labelDesign}
            >
              {props.label}
            </Label>
          )}
          {props.subtitle && (
            <Text
              color={Color.DARK_GREY}
              size={props.labelSize === 'small' ? 6.5 : 6}
            >
              {props.subtitle}
            </Text>
          )}
        </Div>
      )}
      <Body>
        {props.uncheckedLabel && (
          <SwitchLabel htmlFor={id}>{props.uncheckedLabel}</SwitchLabel>
        )}
        <Switch checked={props.checked} disabled={props.disabled} size={size}>
          <Checkbox
            checked={props.checked}
            disabled={props.disabled}
            id={id}
            onChange={(event) => {
              props.onChange?.(event)
              props.onCheckedChange(event.target.checked)
            }}
            type="checkbox"
          />
          <Button checked={props.checked} size={size} />
        </Switch>
        {props.checkedLabel && (
          <SwitchLabel htmlFor={id}>{props.checkedLabel}</SwitchLabel>
        )}
      </Body>
    </ToggleElement>
  )
}
